import { DateTime } from 'luxon';
import moment from 'moment-timezone';

/**
 * Simple date utility helpers to convert UTC dates to local timezone
 * and safely work with date-only values without unexpected timezone shifts.
 * Uses moment-timezone for 3-letter timezone abbreviations (EDT, EST, UTC, etc.).
 */

const DEALER_STORAGE_KEY = 'dealer';
const DEFAULT_TIMEZONE = 'America/New_York';

/**
 * Returns dealer timezone from localStorage (dealer object has time_zone or timezone).
 * Use this wherever dealer-specific timezone is needed for display (e.g. dates/times).
 */
export const getDealerTimezoneFromStorage = (): string => {
  try {
    const stored = localStorage.getItem(DEALER_STORAGE_KEY);
    if (stored) {
      const dealer = JSON.parse(stored) as { time_zone?: string; timezone?: string };
      const tz = dealer?.time_zone || dealer?.timezone;
      if (tz) return tz;
    }
  } catch (_) {}
  return DEFAULT_TIMEZONE;
};

/**
 * Safely parses a date value into a Luxon DateTime instance using the user's local timezone.
 */
export const parseDateValue = (
  value?: string | Date | DateTime | null,
): DateTime | null => {
  if (!value) {
    return null;
  }

  if (DateTime.isDateTime(value)) {
    return value.isValid ? value : null;
  }

  if (value instanceof Date) {
    const parsedDate = DateTime.fromJSDate(value);
    return parsedDate.isValid ? parsedDate : null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();

    if (trimmed) {
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        const parsedDateOnly = DateTime.fromISO(trimmed, {
          zone: 'local',
        }).startOf('day');
        if (parsedDateOnly.isValid) {
          return parsedDateOnly;
        }
      }

      const parsedString = DateTime.fromISO(trimmed, { setZone: true }).toLocal();
      if (parsedString.isValid) {
        return parsedString;
      }
    }
  }

  const parsed = DateTime.fromJSDate(new Date(value as any));
  return parsed.isValid ? parsed : null;
};

/**
 * Formats a date string or Date instance into a display string without timezone shifts.
 */
export const formatDateForDisplay = (
  value?: string | Date | DateTime | null,
  format: string = 'MM/dd/yyyy',
): string => {
  let parsed: DateTime | null = null;

  if (typeof value === 'string') {
    parsed = DateTime.fromISO(value, { setZone: true }).toLocal();
  }

  if (!parsed) {
    parsed = parseDateValue(value);
  }

  return parsed ? parsed.toFormat(format) : '';
};

/**
 * Returns today's date at start of day in the user's local timezone as a Luxon DateTime instance.
 */
export const getTodayStartOfDay = (): DateTime => DateTime.local().startOf('day');

/**
 * Returns today's date at start of day as a native Date object.
 */
export const getTodayStartOfDayDate = (): Date => getTodayStartOfDay().toJSDate();

/**
 * Returns 3-letter timezone abbreviation (EDT, EST, UTC, etc.) for an IANA timezone name.
 * Use this whenever timezone is shown in the UI instead of the full name (e.g. America/New_York).
 */
export const getTimezoneAbbr = (ianaTimezone: string | null | undefined): string => {
  if (!ianaTimezone) return '';
  try {
    return moment().tz(ianaTimezone).format('z');
  } catch {
    return '';
  }
};

/**
 * Converts UTC date string to local timezone with 3-letter timezone abbreviation (EDT, EST, UTC, etc.).
 * @param utcDateString - UTC date string
 * @returns Formatted string like '07 Aug 2025, 03:55 PM EDT'
 *
 * Example usage:
 * formatToLocalTime('2025-08-07T10:25:00Z') // Returns: '07 Aug 2025, 03:55 PM EDT'
 */
export const formatToLocalTime = (utcDateString: string | Date): string => {
  try {
    const m = moment(utcDateString);
    if (!m.isValid()) return 'Invalid Date';
    const tz = moment.tz.guess();
    return m.tz(tz).format('DD MMM YYYY, hh:mm A z');
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
};

/**
 * Formats a date string in dealer's timezone (from localStorage) with 3-letter TZ (EDT, EST, etc.).
 * Use when you need to show date+time in dealer timezone without passing timezone explicitly.
 * @param dateString - ISO date string or Date (e.g. from API)
 * @param includeTzAbbr - If true, appends 3-letter TZ (default true)
 * @returns Formatted string like '10 Oct 2025, 03:55 PM EDT'
 */
export const formatToDealerTime = (
  dateString: string | Date | null | undefined,
  includeTzAbbr: boolean = true,
): string =>
  formatDateWithTimezone(dateString, getDealerTimezoneFromStorage(), includeTzAbbr);

/**
 * Formats a date string in dealer's timezone (from localStorage) as date-only.
 * Use when you need dealer timezone conversion but do not want time/TZ in UI.
 * @param dateString - ISO date string or Date (e.g. from API)
 * @returns Formatted string like '10 Oct 2025'
 */
export const formatToDealerDate = (
  dateString: string | Date | null | undefined,
): string => formatDateWithTimezone(dateString, getDealerTimezoneFromStorage(), false, 'DD MMM YYYY');

/**
 * Formats a date string in UTC with same format as formatToDealerTime (DD MMM YYYY, hh:mm A UTC).
 * @param dateString - ISO date string or Date (e.g. from API)
 * @param includeTzAbbr - If true, appends UTC (default true)
 * @returns Formatted string like '15 Jul 2025, 03:25 AM UTC'
 */
export const formatToUtcTime = (
  dateString: string | Date | null | undefined,
  includeTzAbbr: boolean = true,
): string => formatDateWithTimezone(dateString, 'UTC', includeTzAbbr);

/**
 * Formats a date string in a specific timezone with 3-letter abbreviation (EDT, EST, UTC, etc.).
 * @param dateString - ISO date string (e.g. from API)
 * @param timezone - IANA timezone (e.g. 'America/New_York', 'America/Los_Angeles')
 * @param includeTzAbbr - If true, appends 3-letter TZ e.g. EDT (default true)
 * @returns Formatted string like '10 Oct 2025, 03:55 PM EDT'
 */
export const formatDateWithTimezone = (
  dateString: string | Date | null | undefined,
  timezone: string = 'America/New_York',
  includeTzAbbr: boolean = true,
  formatWithoutTz: string = 'DD MMM YYYY, hh:mm A',
): string => {
  try {
    if (!dateString) return '';

    const m = moment(dateString);
    if (!m.isValid()) return 'Invalid Date';

    const formatStr = includeTzAbbr ? 'DD MMM YYYY, hh:mm A z' : formatWithoutTz;
    return m.tz(timezone).format(formatStr);
  } catch (error) {
    console.error('Error formatting date with timezone:', error);
    return 'Invalid Date';
  }
};
